import { createApi, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { paginate } from '../utils'
import { BasePageRequest, type BasePageResponse, type BaseRequest } from '../models'
import {
  BookingStatus,
  type AccountSchedulingSettings,
  type Booking,
  type EmailTemplate,
  type Location,
  type SchedulingEventType,
  type SchedulingSlot,
  type SchedulingUser,
  type UserSchedulingSettings,
} from '../../models/Scheduling'
import { type CancelBookingPayload, type CreateBookingPayload, type LocationInput, type RescheduleBookingPayload } from './dto'

const _BASE_URL = import.meta.env.REACT_APP_SCHEDULING_API_BASE_URL!

const availabilityUrl = (accountId: string) => `/accounts/${accountId}/availability`
const emailTemplatesUrl = (accountId: string) => `/accounts/${accountId}/email-templates`
const bookingsUrl = (accountId: string) => `/accounts/${accountId}/bookings`
const eventTypesUrl = (accountId: string) => `/accounts/${accountId}/event-types`
const usersUrl = (accountId: string) => `/accounts/${accountId}/users`
const userEventTypesUrl = (accountId: string, userId: string) => `/accounts/${accountId}/users/${userId}/event-types`
const locationsUrl = (accountId: string) => `/accounts/${accountId}/locations`
const userLocationsUrl = (accountId: string, userId: string) => `/accounts/${accountId}/users/${userId}/locations`
const accountSettingsUrl = (accountId: string) => `/accounts/${accountId}/scheduling-settings`
const userSettingsUrl = (accountId: string, userId: string) => `/accounts/${accountId}/users/${userId}/scheduling-settings`

type PaginatedResult<T> = { data?: { data: T[] }; error?: FetchBaseQueryError }

export interface GetAvailabilityRequest extends BaseRequest {
  eventTypeId: string
  userId?: string
  dateFrom: string
  dateTo: string
}

export interface ListBookingsRequest extends BasePageRequest {
  status?: BookingStatus
  userIds?: string[]
  contactIds?: string[]
  eventTypeIds?: string[]
  search?: string
  dateFrom?: Date
  dateTo?: Date
}

export interface GetBookingByIdRequest extends BaseRequest {
  bookingId: string
}

export interface GetSessionBookingsRequest extends BaseRequest {
  sessionId: string
  roomId: string
}

export interface BookingActionRequest extends BaseRequest {
  bookingId: string
}

export interface CreateBookingRequest extends BaseRequest {
  payload: CreateBookingPayload
}

export interface RescheduleBookingRequest extends BaseRequest {
  payload: RescheduleBookingPayload
}

export interface CancelBookingRequest extends BaseRequest {
  payload: CancelBookingPayload
}

export interface GetEventTypeByIdRequest extends BaseRequest {
  eventTypeId: string
}

export interface UpsertEventTypeRequest extends BaseRequest {
  eventType: SchedulingEventType
}

export interface EventTypeActionRequest extends BaseRequest {
  eventTypeId: string
}

export interface GetUserEventTypesRequest extends BaseRequest {
  userId: string
}

export interface GetSchedulingUsersRequest extends BaseRequest {
  eventTypeId: string
}

export interface AddEventTypesToUserPageRequest extends BaseRequest {
  userId: string
  eventTypeIds: string[]
}

export interface RemoveEventTypeFromUserPageRequest extends BaseRequest {
  userId: string
  eventTypeId: string
}

export interface CreateLocationRequest extends BaseRequest {
  input: LocationInput
}

export interface UpdateLocationRequest extends BaseRequest {
  locationId: string
  input: LocationInput
}

export interface LocationActionRequest extends BaseRequest {
  locationId: string
}

export interface GetUserLocationsRequest extends BaseRequest {
  userId: string
}

export interface CreateUserLocationRequest extends BaseRequest {
  userId: string
  input: LocationInput
}

export interface UpdateUserLocationRequest extends BaseRequest {
  userId: string
  locationId: string
  input: LocationInput
}

export interface UserLocationActionRequest extends BaseRequest {
  userId: string
  locationId: string
}

export interface UpdateAccountSchedulingSettingsRequest extends BaseRequest {
  update: Partial<AccountSchedulingSettings>
}

export interface GetUserSchedulingSettingsRequest extends BaseRequest {
  userId: string
}

export interface UpdateUserSchedulingSettingsRequest extends BaseRequest {
  userId: string
  update: Partial<UserSchedulingSettings>
}

export interface ResetUserSchedulingSettingsRequest extends BaseRequest {
  userId: string
}

export const schedulingApi = createApi({
  reducerPath: 'schedulingApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: [
    'Booking',
    'SchedulingEventType',
    'UserSchedulingEventTypes',
    'SchedulingLocation',
    'UserSchedulingLocation',
    'AccountSchedulingSettings',
    'UserSchedulingSettings',
    'SchedulingEmailTemplate',
    'SchedulingUser',
  ],
  endpoints: (builder) => ({
    getSchedulingEmailTemplates: builder.query<EmailTemplate[], BaseRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<EmailTemplate>({ url: emailTemplatesUrl(request.accountId) }, baseQuery)) as PaginatedResult<EmailTemplate>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: ['SchedulingEmailTemplate'],
    }),
    getSchedulingAvailability: builder.query<SchedulingSlot[], GetAvailabilityRequest>({
      query: (request) => ({
        url: availabilityUrl(request.accountId),
        params: {
          eventTypeId: request.eventTypeId,
          dateFrom: request.dateFrom,
          dateTo: request.dateTo,
          ...(request.userId ? { userId: request.userId } : {}),
        },
      }),
      transformResponse: (response: { slots?: SchedulingSlot[] }) => response.slots ?? [],
    }),
    getBookings: builder.query<BasePageResponse<Booking[]>, ListBookingsRequest>({
      query: (request) => ({
        url: bookingsUrl(request.accountId),
        params: {
          ...(request.status ? { status: request.status } : {}),
          ...(request.userIds?.length ? { userIds: request.userIds.join() } : {}),
          ...(request.contactIds?.length ? { contactIds: request.contactIds.join() } : {}),
          ...(request.eventTypeIds?.length ? { eventTypeIds: request.eventTypeIds.join() } : {}),
          ...(request.search ? { search: request.search } : {}),
          ...(request.dateFrom ? { dateFrom: request.dateFrom.toISOString() } : {}),
          ...(request.dateTo ? { dateTo: request.dateTo.toISOString() } : {}),
          ...request.pageRequest,
        },
      }),
      providesTags: (result) => [
        { type: 'Booking', id: 'LIST' },
        ...(result?.data ?? []).map((booking) => ({ type: 'Booking' as const, id: booking.bookingId })),
      ],
    }),
    getAllBookings: builder.query<Booking[], BaseRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<Booking>({ url: bookingsUrl(request.accountId) }, baseQuery)) as PaginatedResult<Booking>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: (result) => [{ type: 'Booking', id: 'LIST' }, ...(result ?? []).map((booking) => ({ type: 'Booking' as const, id: booking.bookingId }))],
    }),
    getSessionBookings: builder.query<Booking[], GetSessionBookingsRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<Booking>(
          { url: bookingsUrl(request.accountId), params: { sourceType: 'session', sourceIds: `sessionId:${request.sessionId},roomId:${request.roomId}` } },
          baseQuery,
        )) as PaginatedResult<Booking>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: (result) => [{ type: 'Booking', id: 'LIST' }, ...(result ?? []).map((booking) => ({ type: 'Booking' as const, id: booking.bookingId }))],
    }),
    getBookingById: builder.query<Booking, GetBookingByIdRequest>({
      query: (request) => `${bookingsUrl(request.accountId)}/${request.bookingId}`,
      providesTags: (_result, _error, request) => [{ type: 'Booking', id: request.bookingId }],
    }),
    acceptBooking: builder.mutation<Booking, BookingActionRequest>({
      query: (request) => ({ url: `${bookingsUrl(request.accountId)}/${request.bookingId}/accept`, method: 'POST' }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'Booking', id: 'LIST' },
        { type: 'Booking', id: request.bookingId },
      ],
    }),
    createBooking: builder.mutation<Booking, CreateBookingRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const { opportunityId, ...body } = request.payload
        if (opportunityId) {
          return { error: { status: 'CUSTOM_ERROR' as const, error: 'Scheduling from an opportunity is not implemented yet' } }
        }
        const result = await baseQuery({ url: bookingsUrl(request.accountId), method: 'POST', body })
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data as Booking }
      },
      invalidatesTags: [{ type: 'Booking', id: 'LIST' }],
    }),
    rescheduleBooking: builder.mutation<Booking, RescheduleBookingRequest>({
      query: (request) => {
        const { bookingId, ...body } = request.payload
        return { url: `${bookingsUrl(request.accountId)}/${bookingId}/reschedule`, method: 'POST', body }
      },
      invalidatesTags: (_result, _error, request) => [
        { type: 'Booking', id: 'LIST' },
        { type: 'Booking', id: request.payload.bookingId },
      ],
    }),
    cancelBooking: builder.mutation<Booking, CancelBookingRequest>({
      query: (request) => {
        const { bookingId, ...body } = request.payload
        return { url: `${bookingsUrl(request.accountId)}/${bookingId}/cancel`, method: 'POST', body }
      },
      invalidatesTags: (_result, _error, request) => [
        { type: 'Booking', id: 'LIST' },
        { type: 'Booking', id: request.payload.bookingId },
      ],
    }),
    sendReminder: builder.mutation<Booking, BookingActionRequest>({
      query: (request) => ({ url: `${bookingsUrl(request.accountId)}/${request.bookingId}/reminders`, method: 'POST' }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'Booking', id: 'LIST' },
        { type: 'Booking', id: request.bookingId },
      ],
    }),
    getSchedulingEventTypes: builder.query<SchedulingEventType[], BaseRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const url = eventTypesUrl(request.accountId)
        const active = (await paginate<SchedulingEventType>({ url, params: { active: true } }, baseQuery)) as PaginatedResult<SchedulingEventType>
        if (active.error) {
          return { error: active.error }
        }
        const inactive = (await paginate<SchedulingEventType>({ url, params: { active: false } }, baseQuery)) as PaginatedResult<SchedulingEventType>
        if (inactive.error) {
          return { error: inactive.error }
        }
        return { data: [...(active.data?.data ?? []), ...(inactive.data?.data ?? [])] }
      },
      providesTags: (result) => [
        { type: 'SchedulingEventType', id: 'LIST' },
        ...(result ?? []).map((eventType) => ({ type: 'SchedulingEventType' as const, id: eventType.id })),
      ],
    }),
    getSchedulingUsers: builder.query<SchedulingUser[], GetSchedulingUsersRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<SchedulingUser>(
          { url: usersUrl(request.accountId), params: { eventTypeId: request.eventTypeId } },
          baseQuery,
        )) as PaginatedResult<SchedulingUser>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: ['SchedulingUser'],
    }),
    getSchedulingEventTypeById: builder.query<SchedulingEventType, GetEventTypeByIdRequest>({
      query: (request) => `${eventTypesUrl(request.accountId)}/${request.eventTypeId}`,
      providesTags: (_result, _error, request) => [{ type: 'SchedulingEventType', id: request.eventTypeId }],
    }),
    upsertSchedulingEventType: builder.mutation<SchedulingEventType, UpsertEventTypeRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const url = eventTypesUrl(request.accountId)
        let eventTypeId = request.eventType.id
        if (!eventTypeId) {
          const created = await baseQuery({ url, method: 'POST', body: request.eventType })
          if (created.error) {
            return { error: created.error }
          }
          eventTypeId = (created.data as SchedulingEventType).id
        }
        const updated = await baseQuery({ url: `${url}/${eventTypeId}`, method: 'PATCH', body: { ...request.eventType, id: eventTypeId } })
        if (updated.error) {
          return { error: updated.error }
        }
        return { data: updated.data as SchedulingEventType }
      },
      invalidatesTags: (result) => [{ type: 'SchedulingEventType', id: 'LIST' }, ...(result ? [{ type: 'SchedulingEventType' as const, id: result.id }] : [])],
    }),
    getUserSchedulingEventTypes: builder.query<string[], GetUserEventTypesRequest>({
      query: (request) => userEventTypesUrl(request.accountId, request.userId),
      transformResponse: (response: { eventTypeIds?: string[] }) => response.eventTypeIds ?? [],
      providesTags: ['UserSchedulingEventTypes'],
    }),
    addSchedulingEventTypesToUserPage: builder.mutation<string[], AddEventTypesToUserPageRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const url = userEventTypesUrl(request.accountId, request.userId)
        const current = await baseQuery(url)
        if (current.error) {
          return { error: current.error }
        }
        const currentIds = (current.data as { eventTypeIds?: string[] }).eventTypeIds ?? []
        const eventTypeIds = Array.from(new Set([...currentIds, ...request.eventTypeIds]))
        const result = await baseQuery({ url, method: 'PUT', body: { eventTypeIds } })
        if (result.error) {
          return { error: result.error }
        }
        return { data: (result.data as { eventTypeIds?: string[] }).eventTypeIds ?? eventTypeIds }
      },
      invalidatesTags: ['UserSchedulingEventTypes', { type: 'SchedulingEventType', id: 'LIST' }],
    }),
    removeSchedulingEventTypeFromUserPage: builder.mutation<string[], RemoveEventTypeFromUserPageRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const url = userEventTypesUrl(request.accountId, request.userId)
        const current = await baseQuery(url)
        if (current.error) {
          return { error: current.error }
        }
        const currentIds = (current.data as { eventTypeIds?: string[] }).eventTypeIds ?? []
        const eventTypeIds = currentIds.filter((id) => id !== request.eventTypeId)
        const result = await baseQuery({ url, method: 'PUT', body: { eventTypeIds } })
        if (result.error) {
          return { error: result.error }
        }
        return { data: (result.data as { eventTypeIds?: string[] }).eventTypeIds ?? eventTypeIds }
      },
      invalidatesTags: ['UserSchedulingEventTypes', { type: 'SchedulingEventType', id: 'LIST' }],
    }),
    duplicateSchedulingEventType: builder.mutation<SchedulingEventType, EventTypeActionRequest>({
      query: (request) => ({ url: `${eventTypesUrl(request.accountId)}/${request.eventTypeId}/duplicate`, method: 'POST' }),
      invalidatesTags: [{ type: 'SchedulingEventType', id: 'LIST' }],
    }),
    deleteSchedulingEventType: builder.mutation<{ id: string }, EventTypeActionRequest>({
      query: (request) => ({ url: `${eventTypesUrl(request.accountId)}/${request.eventTypeId}`, method: 'DELETE' }),
      transformResponse: (_response: unknown, _meta, request) => ({ id: request.eventTypeId }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'SchedulingEventType', id: 'LIST' },
        { type: 'SchedulingEventType', id: request.eventTypeId },
      ],
    }),
    getSchedulingLocations: builder.query<Location[], BaseRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<Location>({ url: locationsUrl(request.accountId) }, baseQuery)) as PaginatedResult<Location>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: (result) => [
        { type: 'SchedulingLocation', id: 'LIST' },
        ...(result ?? []).map((location) => ({ type: 'SchedulingLocation' as const, id: location.id })),
      ],
    }),
    createSchedulingLocation: builder.mutation<Location, CreateLocationRequest>({
      query: (request) => ({ url: locationsUrl(request.accountId), method: 'POST', body: request.input }),
      invalidatesTags: [{ type: 'SchedulingLocation', id: 'LIST' }],
    }),
    updateSchedulingLocation: builder.mutation<Location, UpdateLocationRequest>({
      query: (request) => ({ url: `${locationsUrl(request.accountId)}/${request.locationId}`, method: 'PATCH', body: request.input }),
      invalidatesTags: (result) => [{ type: 'SchedulingLocation', id: 'LIST' }, ...(result ? [{ type: 'SchedulingLocation' as const, id: result.id }] : [])],
    }),
    deleteSchedulingLocation: builder.mutation<{ id: string }, LocationActionRequest>({
      query: (request) => ({ url: `${locationsUrl(request.accountId)}/${request.locationId}`, method: 'DELETE' }),
      transformResponse: (_response: unknown, _meta, request) => ({ id: request.locationId }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'SchedulingLocation', id: 'LIST' },
        { type: 'SchedulingLocation', id: request.locationId },
      ],
    }),
    getUserSchedulingLocations: builder.query<Location[], GetUserLocationsRequest>({
      queryFn: async (request, _api, _extra, baseQuery) => {
        const result = (await paginate<Location>({ url: userLocationsUrl(request.accountId, request.userId) }, baseQuery)) as PaginatedResult<Location>
        if (result.error) {
          return { error: result.error }
        }
        return { data: result.data?.data ?? [] }
      },
      providesTags: (result) => [
        { type: 'UserSchedulingLocation', id: 'LIST' },
        ...(result ?? []).map((location) => ({ type: 'UserSchedulingLocation' as const, id: location.id })),
      ],
    }),
    createUserSchedulingLocation: builder.mutation<Location, CreateUserLocationRequest>({
      query: (request) => ({ url: userLocationsUrl(request.accountId, request.userId), method: 'POST', body: request.input }),
      invalidatesTags: [{ type: 'UserSchedulingLocation', id: 'LIST' }],
    }),
    updateUserSchedulingLocation: builder.mutation<Location, UpdateUserLocationRequest>({
      query: (request) => ({ url: `${userLocationsUrl(request.accountId, request.userId)}/${request.locationId}`, method: 'PATCH', body: request.input }),
      invalidatesTags: (result) => [
        { type: 'UserSchedulingLocation', id: 'LIST' },
        ...(result ? [{ type: 'UserSchedulingLocation' as const, id: result.id }] : []),
      ],
    }),
    deleteUserSchedulingLocation: builder.mutation<{ id: string }, UserLocationActionRequest>({
      query: (request) => ({ url: `${userLocationsUrl(request.accountId, request.userId)}/${request.locationId}`, method: 'DELETE' }),
      transformResponse: (_response: unknown, _meta, request) => ({ id: request.locationId }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'UserSchedulingLocation', id: 'LIST' },
        { type: 'UserSchedulingLocation', id: request.locationId },
      ],
    }),
    getAccountSchedulingSettings: builder.query<AccountSchedulingSettings, BaseRequest>({
      query: (request) => accountSettingsUrl(request.accountId),
      providesTags: ['AccountSchedulingSettings'],
    }),
    updateAccountSchedulingSettings: builder.mutation<AccountSchedulingSettings, UpdateAccountSchedulingSettingsRequest>({
      query: (request) => ({ url: accountSettingsUrl(request.accountId), method: 'PATCH', body: request.update }),
      invalidatesTags: ['AccountSchedulingSettings'],
    }),
    getUserSchedulingSettings: builder.query<UserSchedulingSettings, GetUserSchedulingSettingsRequest>({
      query: (request) => userSettingsUrl(request.accountId, request.userId),
      providesTags: ['UserSchedulingSettings'],
    }),
    updateUserSchedulingSettings: builder.mutation<UserSchedulingSettings, UpdateUserSchedulingSettingsRequest>({
      query: (request) => ({ url: userSettingsUrl(request.accountId, request.userId), method: 'PATCH', body: request.update }),
      invalidatesTags: ['UserSchedulingSettings'],
    }),
    resetUserSchedulingSettings: builder.mutation<UserSchedulingSettings, ResetUserSchedulingSettingsRequest>({
      query: (request) => ({ url: `${userSettingsUrl(request.accountId, request.userId)}/reset`, method: 'POST' }),
      invalidatesTags: ['UserSchedulingSettings'],
    }),
  }),
})

export const {
  useGetSchedulingEmailTemplatesQuery,
  useGetSchedulingAvailabilityQuery,
  useGetBookingsQuery,
  useGetSessionBookingsQuery,
  useGetAllBookingsQuery,
  useGetBookingByIdQuery,
  useAcceptBookingMutation,
  useCreateBookingMutation,
  useRescheduleBookingMutation,
  useCancelBookingMutation,
  useSendReminderMutation,
  useGetSchedulingEventTypesQuery,
  useGetSchedulingUsersQuery,
  useGetSchedulingEventTypeByIdQuery,
  useUpsertSchedulingEventTypeMutation,
  useGetUserSchedulingEventTypesQuery,
  useAddSchedulingEventTypesToUserPageMutation,
  useRemoveSchedulingEventTypeFromUserPageMutation,
  useDuplicateSchedulingEventTypeMutation,
  useDeleteSchedulingEventTypeMutation,
  useGetSchedulingLocationsQuery,
  useCreateSchedulingLocationMutation,
  useUpdateSchedulingLocationMutation,
  useDeleteSchedulingLocationMutation,
  useGetUserSchedulingLocationsQuery,
  useCreateUserSchedulingLocationMutation,
  useUpdateUserSchedulingLocationMutation,
  useDeleteUserSchedulingLocationMutation,
  useGetAccountSchedulingSettingsQuery,
  useUpdateAccountSchedulingSettingsMutation,
  useGetUserSchedulingSettingsQuery,
  useUpdateUserSchedulingSettingsMutation,
  useResetUserSchedulingSettingsMutation,
} = schedulingApi
